import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, Dialog, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Customer } from '../../my-lemonade-library/src/authentications/models/Customer';
import { fixPhone } from '../../my-lemonade-library/src/common/services/AddressService';
import { RootState, useTypedSelector } from '../../redux/root-reducer';

export type UserEditableField = 'first_name' | 'last_name' | 'phone';

interface UpdateUserInfosProps {
    open: boolean
    handleOpen: (bool: boolean) => void
    userParameter: UserEditableField
    customer: Customer
    onSave: (customer: Customer) => void
}

const UpdateUserInfos = (props: UpdateUserInfosProps) => {
    const {
        open,
        handleOpen,
        userParameter,
        customer,
        onSave
    } = props

    const intl = useIntl()
    const theme = useTheme()
    const { selectedLocation } = useTypedSelector((state: RootState) => state.locations);
    const countryCode = selectedLocation?.country || 'fr';

    const [updatedCustomer, setUpdatedCustomer] = useState<Customer>(customer)

    const getCurrentValue = (): string => {
        return updatedCustomer[userParameter] || '';
    }

    const handleChange = (value: string) => {
        if (userParameter === 'phone') {
            const phoneObject = { phone: value };
            fixPhone(phoneObject, countryCode);
            setUpdatedCustomer({
                ...updatedCustomer,
                phone: phoneObject.phone
            });
        } else {
            setUpdatedCustomer({
                ...updatedCustomer,
                [userParameter]: value
            });
        }
    }

    const handleSave = () => {
        onSave(updatedCustomer);
        handleOpen(false);
    }

    return (
        <Dialog
            open={open}
            onClose={() => handleOpen(false)}
        >
            <Box
                display="flex"
                flexDirection="column"
                justifyContent='center'
                alignItems='center'
                textAlign='start'
                marginTop={1}
                p={3}
                gap={2}
            >
                <IconButton
                    style={{
                        position: 'absolute',
                        color: theme.palette.text.disabled,
                        top: 0,
                        right: 0,
                    }}
                    onClick={() => handleOpen(false)}
                >
                    <CloseRoundedIcon />
                </IconButton>

                <Typography variant='subtitle1'>
                    {intl.formatMessage({ id: `auth.update.${userParameter}` })}
                </Typography>

                {userParameter === 'phone' ? (
                    <Box width="100%">
                        <ReactPhoneInput
                            country={countryCode}
                            value={getCurrentValue()}
                            onChange={handleChange}
                            enableSearch={true}
                            placeholder={intl.formatMessage({ id: `auth.update.placeholder.${userParameter}` })}
                            inputStyle={{
                                width: '100%',
                                height: '56px',
                                fontSize: '16px',
                                fontFamily: theme.typography.fontFamily,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: theme.shape.borderRadius,
                                paddingLeft: '52px',
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                outline: 'none',
                                transition: 'border-color 0.2s ease-in-out',
                            }}
                        />
                    </Box>
                ) : (
                    <TextField
                        value={getCurrentValue()}
                        onChange={(e) => handleChange(e.target.value)}
                        fullWidth={true}
                        variant='outlined'
                        type={getCurrentValue()}
                        placeholder={intl.formatMessage({ id: `auth.update.placeholder.${userParameter}` })}
                    />
                )}

                <Button
                    variant="contained"
                    color="primary"
                    style={{
                        textTransform: "none",
                        width: "80%",
                        padding: theme.spacing(1, 0),
                    }}
                    onClick={handleSave}
                >
                    {intl.formatMessage({ id: 'common.save' })}
                </Button>
            </Box>
        </Dialog>
    )
}

export default UpdateUserInfos